import {
  normalizeQuestionRequirements,
  type QuestionRequirementsRichText,
} from '@wx-weiqi/contracts';
import type { ClassroomNoteFormat } from './classroom-note-format';

// Format text leaves without extracting block elements: extracting a selection
// across paragraphs can leave empty DIVs behind and silently add blank lines.
export function formatRequirementsRange(
  editor: HTMLElement,
  selection: Range,
  format: ClassroomNoteFormat,
): Range {
  const ranges: Range[] = [];
  const walker = document.createTreeWalker(editor, NodeFilter.SHOW_TEXT);
  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    if (!node.textContent || !selection.intersectsNode(node)) continue;
    const start = node === selection.startContainer ? selection.startOffset : 0;
    const end = node === selection.endContainer ? selection.endOffset : node.textContent.length;
    if (start >= end) continue;
    const range = document.createRange();
    range.setStart(node, start);
    range.setEnd(node, end);
    ranges.push(range);
  }
  const spans: HTMLSpanElement[] = [];
  for (const range of ranges.reverse()) {
    const span = document.createElement('span');
    Object.assign(span.style, format);
    range.surroundContents(span);
    spans.unshift(span);
  }
  const first = spans.at(0);
  const last = spans.at(-1);
  if (!first || !last) return selection;
  const result = document.createRange();
  result.setStart(first, 0);
  result.setEnd(last, last.childNodes.length);
  return result;
}

export function readRequirementsEditor(editor: HTMLElement) {
  const runs: QuestionRequirementsRichText = [];
  const append = (text: string, parent: HTMLElement) => {
    if (!text) return;
    const style = getComputedStyle(parent);
    runs.push({
      text,
      bold: Number.parseInt(style.fontWeight, 10) >= 600,
      color: style.color === 'rgb(181, 58, 50)' ? 'RED' : 'BLACK',
    });
  };
  const walk = (parent: HTMLElement) => {
    for (const child of parent.childNodes) {
      if (child.nodeType === Node.TEXT_NODE) append(child.textContent ?? '', parent);
      else if (child instanceof HTMLElement) {
        if (['SCRIPT', 'STYLE'].includes(child.tagName)) continue;
        if (child.tagName === 'BR') {
          append('\n', parent);
          continue;
        }
        const block = ['DIV', 'P', 'LI'].includes(child.tagName);
        if (block && child.previousSibling) append('\n', parent);
        if (!(block && child.childNodes.length === 1 && child.firstChild?.nodeName === 'BR'))
          walk(child);
        if (block && child.nextSibling?.nodeType === Node.TEXT_NODE) append('\n', parent);
      }
    }
  };
  walk(editor);
  return normalizeQuestionRequirements(runs);
}

export function writeRequirementsEditor(
  editor: HTMLElement,
  text: string,
  richText?: QuestionRequirementsRichText | null,
) {
  const runs = richText?.map((run) => run.text).join('') === text ? richText : [{ text }];
  editor.replaceChildren(
    ...(runs ?? []).map((run) => {
      const span = document.createElement('span');
      span.textContent = run.text;
      span.style.fontWeight = run.bold ? '700' : '400';
      span.style.color = run.color === 'RED' ? '#b53a32' : '#000000';
      return span;
    }),
  );
}
